import { HugeiconsIcon } from '@hugeicons/react';
import {
  SentIcon,
  Loading02Icon,
  SparklesIcon,
  DatabaseIcon,
  AlertCircleIcon,
  Copy01Icon,
  Tick02Icon,
  Download01Icon,
  ThumbsUpIcon,
  // ThumbsDownIcon confirmado presente en @hugeicons/core-free-icons@^4.2.3.
  ThumbsDownIcon,
  RefreshIcon,
  Add01Icon,
  Message01Icon,
  Delete02Icon,
  Menu01Icon,
  // Iconos del sidebar: combobox de agente, búsqueda y menú por conversación.
  Search01Icon,
  Tick01Icon,
  ArrowDown01Icon,
  MoreHorizontalIcon,
  Edit02Icon,
  // Icono del badge del encabezado de pagina: coherente con la entrada "VIA
  // Insights" del menu global (WebLayout usa AiInnovation01Icon para ella).
  AiInnovation01Icon,
} from '@hugeicons/core-free-icons';
import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { api, apiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { MotionSection } from '@/components/shared/MotionSection';
import SqlBlock from '@/components/insights/SqlBlock';
import { toast } from 'sonner';

// VIA Insights — chat conversacional sobre los datos del Data Warehouse
// (Redshift) mediante agentes especializados por dominio. Solo lectura.
// Consume POST /api/insights/chat (worker redshift-agent-worker en el backend),
// más /insights/agents/available y /insights/conversations* para selector de
// agentes e historial persistido (estilo ChatGPT).

const SUGERENCIAS = [
  '¿Cuántos registros tiene la tabla de facturación comercial?',
  'Dame un conteo de ventas por canal digital',
  '¿Qué tablas hay disponibles en el dominio comercial?',
];

// El chat identifica el agente por su UUID (insights_domains.id), la clave
// canónica de autorización y selección. El backend traduce ese UUID al slug que
// el worker entiende justo antes de invocarlo (workerAgentSlug en insights.go),
// así que el frontend ya no deriva ningún slug. Si /insights/agents/available
// devuelve lista vacía o falla, no hay agente seleccionable y el envío queda
// deshabilitado (CA-6/CA-18): sin agentes accesibles no se puede chatear.

// mapearMensajesBackend convierte los mensajes persistidos ({role, content,
// sql, table, model}) al shape que renderiza el hilo. Para cada mensaje de
// agente toma como `question` el mensaje de usuario inmediatamente anterior,
// para que "Regenerar" y el feedback de pulgares sigan funcionando al recargar.
// agentId es el valor persistido en insights_conversations.agent_id: de aquí en
// adelante es el UUID del agente; en hilos legacy puede ser un slug ("comercial")
// o vacío. Se propaga tal cual (msg.agent/msg.domain) para el feedback analítico.
// Función pura (sin estado) para facilitar un test futuro.
function mapearMensajesBackend(mensajes, agentId) {
  const salida = [];
  let preguntaPrevia = '';
  for (const m of mensajes || []) {
    if (m.role === 'user') {
      preguntaPrevia = m.content || '';
      salida.push({ role: 'user', content: m.content || '' });
    } else if (m.role === 'agent') {
      salida.push({
        role: 'agent',
        question: preguntaPrevia,
        agent: agentId,
        domain: agentId,
        answer: m.content || '',
        sql: m.sql,
        table: m.table,
        model: m.model,
      });
    }
  }
  return salida;
}

function TablaResultado({ table }) {
  if (!table || !table.columns?.length) return null;
  return (
    <div className="mt-3 overflow-x-auto rounded-xl border border-border/80">
      <table className="w-full text-sm">
        <thead className="bg-muted/40">
          <tr>
            {table.columns.map((c) => (
              <th key={c} className="px-3 py-2 text-left font-semibold text-text">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows?.slice(0, 100).map((row, i) => (
            <tr key={i} className="border-t border-border/60">
              {row.map((cell, j) => (
                <td key={j} className="px-3 py-2 text-body">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Convierte la tabla de resultado ({columns, rows}) a texto CSV con comillas
// seguras (escapa comillas dobles y envuelve celdas con comas/saltos de línea).
function tablaACSV(table) {
  if (!table?.columns?.length) return '';
  const escaparCelda = (valor) => {
    const texto = valor == null ? '' : String(valor);
    return /[",\n]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
  };
  const encabezado = table.columns.map(escaparCelda).join(',');
  const filas = (table.rows || []).map((row) => row.map(escaparCelda).join(','));
  return [encabezado, ...filas].join('\n');
}

// Botón de acción solo-icono para la barra estilo ChatGPT. Expone aria-label
// obligatorio (WCAG) y oculta el icono decorativo a lectores de pantalla.
function BotonAccion({ icon, label, onClick, disabled, activo }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={`flex h-8 w-8 items-center justify-center rounded-md border border-transparent text-body
        transition hover:bg-muted hover:text-text focus:outline-none focus-visible:ring-2
        focus-visible:ring-primary focus-visible:ring-offset-1 active:scale-95
        disabled:cursor-not-allowed disabled:opacity-40 ${activo ? 'bg-primary/10 text-primary' : ''}`}>
      <HugeiconsIcon icon={icon} size={16} strokeWidth={1.5} aria-hidden={true} />
    </button>
  );
}

function MensajeAgente({ msg, onRegenerate }) {
  const [copiado, setCopiado] = useState(false); // feedback efímero del botón copiar respuesta
  const [enviandoFeedback, setEnviandoFeedback] = useState(false);
  const [rating, setRating] = useState(null); // 'up' | 'down' | null (pulgar activo)

  // Copia la respuesta en texto plano al portapapeles con feedback efímero.
  const copiarRespuesta = async () => {
    try {
      await navigator.clipboard.writeText(msg.answer || '');
      setCopiado(true);
      toast.success('Copiado');
      setTimeout(() => setCopiado(false), 1500);
    } catch {
      toast.error('No se pudo copiar la respuesta');
    }
  };

  // Copia la tabla de resultado como CSV al portapapeles.
  const copiarCSV = async () => {
    try {
      await navigator.clipboard.writeText(tablaACSV(msg.table));
      toast.success('Tabla copiada como CSV');
    } catch {
      toast.error('No se pudo copiar la tabla');
    }
  };

  // Envía el feedback de pulgar (up/down) al backend. Un fallo no rompe la UI:
  // solo muestra un toast de error y revierte el estado del pulgar.
  const enviarFeedback = async (valor) => {
    if (enviandoFeedback) return;
    const previo = rating;
    setRating(valor);
    setEnviandoFeedback(true);
    try {
      // MEDIUM-2: el feedback envía el UUID del agente del mensaje (sin el
      // literal "comercial"). insights_feedback.agent es solo analítico, así que
      // la mezcla slug/UUID en filas antiguas es aceptable sin migración.
      await api.post('/insights/feedback', {
        question: msg.question,
        sql: msg.sql,
        agent: msg.agent || msg.domain || undefined,
        rating: valor,
      });
      toast.success(valor === 'up' ? 'Gracias por tu valoración' : 'Gracias, lo tendremos en cuenta');
    } catch (e) {
      setRating(previo);
      toast.error(apiError(e) || 'No se pudo registrar tu valoración');
    } finally {
      setEnviandoFeedback(false);
    }
  };

  const hayTabla = Boolean(msg.table?.columns?.length);

  return (
    <div className="flex gap-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <HugeiconsIcon icon={SparklesIcon} size={18} strokeWidth={1.5} aria-hidden={true} />
      </div>
      <div className="min-w-0 flex-1">
        {msg.error ? (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
            <HugeiconsIcon icon={AlertCircleIcon} size={18} strokeWidth={1.5} aria-hidden={true} />
            <span>{msg.error}</span>
          </div>
        ) : (
          <>
            <div
              className="prose prose-sm max-w-none text-sm leading-relaxed text-text
                prose-headings:text-text prose-headings:font-semibold
                prose-p:text-text prose-strong:text-text prose-li:text-text
                prose-table:text-text prose-code:text-text">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.answer || ''}</ReactMarkdown>
            </div>
            <TablaResultado table={msg.table} />
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              {msg.domain && (
                <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-body">
                  <HugeiconsIcon icon={DatabaseIcon} size={14} strokeWidth={1.5} aria-hidden={true} />
                  dominio: {msg.domain}
                </span>
              )}
              {typeof msg.rowCount === 'number' && (
                <span className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-body">
                  {msg.rowCount} fila(s)
                </span>
              )}
              {msg.truncated && (
                <span className="inline-flex items-center rounded-full border border-warning/40 bg-warning/10 px-2.5 py-0.5 text-warning">
                  resultado truncado
                </span>
              )}
            </div>
            {/* Bloque SQL con resaltado de sintaxis (reemplaza el <pre><code>). */}
            <SqlBlock sql={msg.sql} />

            {/* Barra de acciones estilo ChatGPT. Solo para respuestas válidas
                (sin msg.error, garantizado por el bloque superior). */}
            <div className="mt-3 flex items-center gap-1">
              <BotonAccion
                icon={copiado ? Tick02Icon : Copy01Icon}
                label={copiado ? 'Respuesta copiada' : 'Copiar respuesta'}
                onClick={copiarRespuesta}
              />
              {hayTabla && <BotonAccion icon={Download01Icon} label="Copiar tabla como CSV" onClick={copiarCSV} />}
              <BotonAccion
                icon={ThumbsUpIcon}
                label="Buena respuesta"
                onClick={() => enviarFeedback('up')}
                disabled={enviandoFeedback}
                activo={rating === 'up'}
              />
              <BotonAccion
                icon={ThumbsDownIcon}
                label="Mala respuesta"
                onClick={() => enviarFeedback('down')}
                disabled={enviandoFeedback}
                activo={rating === 'down'}
              />
              {msg.question && (
                <BotonAccion
                  icon={RefreshIcon}
                  label="Regenerar respuesta"
                  onClick={() => onRegenerate?.(msg.question)}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// SelectorAgentes — combobox del design system (Popover + Command) que replica
// el patrón SearchableSelect del Toolkit. Reemplaza el <select> nativo roto:
// muestra el agente seleccionado en el trigger, permite buscar por nombre y
// marca el activo con un check. Usa tokens del tema (claro/oscuro). Recibe
// agents, selectedAgent y onSelect; con lista vacía muestra "Sin agentes".
function SelectorAgentes({ agents, agentsState, selectedAgent, onSelect }) {
  const [open, setOpen] = useState(false);
  const seleccionado = agents.find((a) => a.id === selectedAgent?.id) || selectedAgent;

  return (
    <div className="flex flex-col gap-1.5 px-3 py-2">
      <Label htmlFor="insights-agente" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Agente
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="insights-agente"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={agentsState.loading}
            className="w-full justify-between px-3 font-normal">
            <span className="flex min-w-0 items-center gap-2">
              <HugeiconsIcon
                icon={DatabaseIcon}
                size={15}
                strokeWidth={1.5}
                aria-hidden={true}
                className="shrink-0 text-muted-foreground"
              />
              <span className="truncate">{seleccionado?.name || 'Seleccionar…'}</span>
            </span>
            <HugeiconsIcon
              icon={ArrowDown01Icon}
              size={15}
              strokeWidth={1.5}
              aria-hidden={true}
              className="shrink-0 text-muted-foreground"
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
          <Command>
            <CommandInput placeholder="Buscar agente…" />
            <CommandList>
              <CommandEmpty>Sin agentes</CommandEmpty>
              <CommandGroup>
                {agents.map((a) => (
                  <CommandItem
                    key={a.id}
                    value={`${a.name} ${a.id}`}
                    onSelect={() => {
                      onSelect(a);
                      setOpen(false);
                    }}>
                    <HugeiconsIcon
                      icon={Tick01Icon}
                      size={16}
                      strokeWidth={1.5}
                      aria-hidden={true}
                      className={cn('mr-2 text-primary', seleccionado?.id === a.id ? 'opacity-100' : 'opacity-0')}
                    />
                    {a.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {agentsState.error && <p className="mt-1 text-xs text-muted-foreground">{agentsState.error}</p>}
    </div>
  );
}

// recenciaConversacion devuelve el timestamp de recencia de una conversación:
// updated_at si existe, con fallback a created_at. Devuelve 0 (epoch) si ninguno
// es parseable, para que esas filas caigan en "Más antiguos" sin romper el orden.
function recenciaConversacion(c) {
  const raw = c?.updated_at || c?.created_at;
  const ms = raw ? new Date(raw).getTime() : NaN;
  return Number.isFinite(ms) ? ms : 0;
}

// Orden de las secciones del historial (estilo ChatGPT), de más reciente a más
// antigua. agruparConversaciones respeta este orden al emitir los grupos.
const ORDEN_GRUPOS = ['Hoy', 'Ayer', 'Últimos 7 días', 'Últimos 30 días', 'Más antiguos'];

// agruparConversaciones ordena por recencia desc y clasifica cada conversación
// en una sección por antigüedad, comparando contra el inicio del día de hoy.
// Función pura: devuelve [{ label, items }] solo con los grupos NO vacíos, en el
// orden de ORDEN_GRUPOS.
function agruparConversaciones(conversations) {
  const ahora = new Date();
  const inicioHoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate()).getTime();
  const unDia = 24 * 60 * 60 * 1000;
  const inicioAyer = inicioHoy - unDia;
  const hace7 = inicioHoy - 7 * unDia;
  const hace30 = inicioHoy - 30 * unDia;

  const clasificar = (ms) => {
    if (ms >= inicioHoy) return 'Hoy';
    if (ms >= inicioAyer) return 'Ayer';
    if (ms >= hace7) return 'Últimos 7 días';
    if (ms >= hace30) return 'Últimos 30 días';
    return 'Más antiguos';
  };

  const ordenadas = [...conversations].sort((a, b) => recenciaConversacion(b) - recenciaConversacion(a));
  const buckets = new Map();
  for (const c of ordenadas) {
    const label = clasificar(recenciaConversacion(c));
    if (!buckets.has(label)) buckets.set(label, []);
    buckets.get(label).push(c);
  }
  return ORDEN_GRUPOS.filter((label) => buckets.has(label)).map((label) => ({ label, items: buckets.get(label) }));
}

// ItemConversacion — una fila del historial: botón para abrir el hilo + menú
// kebab (Renombrar/Borrar) del design system. aria-current en el activo.
function ItemConversacion({ conversation: c, activo, onOpen, onRename, onDelete }) {
  const titulo = c.title || 'Sin título';
  return (
    <li className="group relative">
      <button
        type="button"
        onClick={() => onOpen(c.id)}
        aria-current={activo ? 'true' : undefined}
        className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 pr-9 text-left text-sm transition
          focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background
          ${activo ? 'border border-primary/40 bg-primary/10 font-medium text-primary' : 'border border-transparent text-foreground hover:bg-muted/60'}`}>
        <HugeiconsIcon icon={Message01Icon} size={16} strokeWidth={1.5} aria-hidden={true} className="shrink-0" />
        <span className="truncate">{titulo}</span>
      </button>
      <div className="absolute right-1 top-1/2 -translate-y-1/2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Opciones de la conversación: ${titulo}`}
              title="Opciones"
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground opacity-0 transition hover:bg-muted hover:text-foreground focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background group-hover:opacity-100 data-[state=open]:opacity-100">
              <HugeiconsIcon icon={MoreHorizontalIcon} size={16} strokeWidth={1.5} aria-hidden={true} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuItem onSelect={() => onRename(c)}>
              <HugeiconsIcon icon={Edit02Icon} size={16} strokeWidth={1.5} aria-hidden={true} />
              Renombrar
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onDelete(c.id)} className="text-destructive focus:text-destructive">
              <HugeiconsIcon icon={Delete02Icon} size={16} strokeWidth={1.5} aria-hidden={true} />
              Borrar
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </li>
  );
}

// HistorialConversaciones — lista los hilos del usuario agrupados por recencia
// (estilo ChatGPT). Recibe la lista YA filtrada por el buscador; distingue el
// estado vacío real ("Aún no tienes conversaciones.") del "Sin resultados" de
// una búsqueda sin coincidencias.
function HistorialConversaciones({
  conversations,
  convState,
  activeConversationId,
  hayBusqueda,
  totalConversations,
  onOpen,
  onRename,
  onDelete,
}) {
  const grupos = useMemo(() => agruparConversaciones(conversations), [conversations]);

  return (
    <nav aria-label="Historial de conversaciones" className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
      <p className="px-1 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Historial</p>
      {convState.loading && (
        <div
          className="flex items-center gap-2 px-1 py-2 text-xs text-muted-foreground"
          role="status"
          aria-live="polite">
          <HugeiconsIcon icon={Loading02Icon} size={14} strokeWidth={1.5} className="animate-spin" aria-hidden={true} />
          Cargando…
        </div>
      )}
      {convState.error && !convState.loading && (
        <p className="px-1 py-2 text-xs text-muted-foreground">{convState.error}</p>
      )}
      {/* Estado vacío real: no hay ninguna conversación persistida. */}
      {!convState.loading && !convState.error && totalConversations === 0 && (
        <p className="px-1 py-2 text-xs text-muted-foreground">Aún no tienes conversaciones.</p>
      )}
      {/* Búsqueda sin coincidencias: sí hay conversaciones pero ninguna coincide. */}
      {!convState.loading &&
        !convState.error &&
        totalConversations > 0 &&
        conversations.length === 0 &&
        hayBusqueda && <p className="px-1 py-2 text-xs text-muted-foreground">Sin resultados</p>}
      <div className="space-y-3">
        {grupos.map((grupo) => (
          <div key={grupo.label}>
            <p className="px-1 pb-1 text-xs uppercase tracking-wide text-muted-foreground">{grupo.label}</p>
            <ul className="space-y-1">
              {grupo.items.map((c) => (
                <ItemConversacion
                  key={c.id}
                  conversation={c}
                  activo={c.id === activeConversationId}
                  onOpen={onOpen}
                  onRename={onRename}
                  onDelete={onDelete}
                />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  );
}

// InsightsSidebar — columna izquierda del chat. Usa tokens del tema (claro en
// modo claro, oscuro en modo oscuro) para integrarse con el resto de la app,
// no una superficie oscura fija. Contiene nueva conversación, selector de
// agentes e historial.
function InsightsSidebar({
  agents,
  agentsState,
  selectedAgent,
  onSelectAgent,
  conversations,
  convState,
  activeConversationId,
  onNueva,
  onOpen,
  onRename,
  onDelete,
}) {
  // Búsqueda de chats: estado local al sidebar. Filtra el historial por título
  // (substring case-insensitive) antes de agruparlo por recencia.
  const [query, setQuery] = useState('');
  const normalizada = query.trim().toLowerCase();
  const conversacionesFiltradas = useMemo(
    () =>
      normalizada
        ? conversations.filter((c) => (c.title || 'Sin título').toLowerCase().includes(normalizada))
        : conversations,
    [conversations, normalizada],
  );

  return (
    <div className="flex h-full flex-col bg-muted/30 text-foreground">
      <div className="px-3 pb-2 pt-4">
        <button
          type="button"
          onClick={onNueva}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-background px-3 py-2.5 text-sm font-medium text-foreground transition hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background">
          <HugeiconsIcon icon={Add01Icon} size={16} strokeWidth={1.5} aria-hidden={true} />
          Nueva conversación
        </button>
      </div>
      <SelectorAgentes
        agents={agents}
        agentsState={agentsState}
        selectedAgent={selectedAgent}
        onSelect={onSelectAgent}
      />
      <div className="mx-3 border-t border-border" />
      {/* Buscador de chats: encima del historial, filtra en cliente. */}
      <div className="px-3 py-2">
        <Label htmlFor="insights-buscar-chats" className="sr-only">
          Buscar chats
        </Label>
        <div className="relative">
          <HugeiconsIcon
            icon={Search01Icon}
            size={15}
            strokeWidth={1.5}
            aria-hidden={true}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            id="insights-buscar-chats"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar chats…"
            className="h-9 pl-8"
          />
        </div>
      </div>
      <HistorialConversaciones
        conversations={conversacionesFiltradas}
        convState={convState}
        activeConversationId={activeConversationId}
        hayBusqueda={normalizada.length > 0}
        totalConversations={conversations.length}
        onOpen={onOpen}
        onRename={onRename}
        onDelete={onDelete}
      />
    </div>
  );
}

export default function Insights() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  // Selector de agentes (endpoint no-admin, proyección {id,name,executable}).
  // Sin fallback a "comercial": si no hay agentes accesibles la lista queda
  // vacía y el envío se deshabilita (CA-6/CA-18). La selección actual la mantiene
  // un estado local; la LISTA la gobierna react-query (ver más abajo) para que
  // se refresque al enfocar la ventana y tras mutaciones de acceso del admin,
  // sin polling agresivo. La seguridad real NO depende de esta lista cacheada:
  // vive en el 403 por request del backend.
  const [selectedAgent, setSelectedAgent] = useState(null);

  // Historial lateral de conversaciones del usuario.
  const [conversations, setConversations] = useState([]);
  const [convState, setConvState] = useState({ loading: true, error: '' });
  const [activeConversationId, setActiveConversationId] = useState(null);

  // Responsive: colapso del sidebar en móvil (overlay deslizante).
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  // Carga de agentes disponibles (ya filtrados por acceso efectivo en el
  // backend; proyección {id,name,executable}) con react-query. queryKey estable
  // ['insights','agents','available'] para que las mutaciones de acceso del admin
  // (AgentAccessPanel / GroupsManager) la invaliden y el usuario vea aparecer o
  // desaparecer agentes sin cerrar sesión ni recargar. refetchOnWindowFocus: true
  // SOLO para esta query (el default global es false) refresca la lista al volver
  // a enfocar la pestaña, cubriendo las revocaciones hechas por otro admin sin
  // polling. Sin fallback a "comercial": lista vacía o error dejan el selector
  // vacío y el envío deshabilitado (CA-6/CA-18).
  const agentsQuery = useQuery({
    queryKey: ['insights', 'agents', 'available'],
    queryFn: async () => {
      const { data } = await api.get('/insights/agents/available');
      return Array.isArray(data) ? data : [];
    },
    refetchOnWindowFocus: true,
    // Un error de la query no debe romper el selector: se trata como "sin
    // agentes" (lista vacía), igual que el comportamiento previo del catch.
  });
  const agents = agentsQuery.data ?? [];
  // agentsState conserva el shape { loading, error } que consumen SelectorAgentes
  // e InsightsSidebar. error siempre vacío: un fallo degrada a "Sin agentes".
  const agentsState = { loading: agentsQuery.isLoading, error: '' };

  // Preselección del primer agente SIN pisar la elección del usuario: solo
  // selecciona lista[0] cuando aún no hay selección o la seleccionada ya no está
  // en la lista nueva (p. ej. tras un refetch que revocó su acceso).
  useEffect(() => {
    setSelectedAgent((prev) => {
      if (prev && agents.some((a) => a.id === prev.id)) return prev;
      return agents[0] ?? null;
    });
  }, [agents]);

  // Carga del historial de conversaciones del usuario.
  const loadConversations = useCallback(async () => {
    setConvState({ loading: true, error: '' });
    try {
      const { data } = await api.get('/insights/conversations');
      setConversations(Array.isArray(data) ? data : []);
      setConvState({ loading: false, error: '' });
    } catch (e) {
      setConversations([]);
      setConvState({ loading: false, error: apiError(e) });
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Nueva conversación: solo limpia el estado local; la conversación se
  // materializa en el backend al enviar la primera pregunta (devuelve el id).
  const nuevaConversacion = () => {
    setMessages([]);
    setActiveConversationId(null);
    setSidebarOpen(false);
  };

  // Abrir un hilo: carga mensajes y los mapea al shape de render.
  const abrirConversacion = async (id) => {
    setSidebarOpen(false);
    setActiveConversationId(id);
    setLoading(true);
    try {
      const { data } = await api.get(`/insights/conversations/${id}`);
      // agent_id persistido: UUID en hilos nuevos; slug/vacío en legacy. No se
      // fuerza ningún default; los mensajes se renderizan igual. El historial
      // legacy no preselecciona agente en el selector (el usuario elige del
      // selector filtrado antes de reenviar).
      const agentId = data?.conversation?.agent_id || null;
      setMessages(mapearMensajesBackend(data?.messages, agentId));
    } catch (e) {
      toast.error(apiError(e) || 'No se pudo abrir la conversación');
    } finally {
      setLoading(false);
    }
  };

  // Diálogo de renombrar: guarda la conversación objetivo y el borrador del
  // título. El input recibe foco al abrir el diálogo (ver autoFocus abajo).
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);

  // Abre el diálogo de renombrar prellenado con el título actual.
  const abrirRenombrar = (conversation) => {
    setRenameTarget(conversation);
    setRenameValue(conversation.title || '');
  };

  // Confirma el renombrado: PATCH al backend y refresco optimista del estado.
  // Como el backend aún no está desplegado en prd, un 404/405 se maneja con un
  // toast de error sin romper la UI (ver nota de alcance en el plan).
  const confirmarRenombrar = async (e) => {
    e?.preventDefault();
    if (!renameTarget) return;
    const titulo = renameValue.trim();
    if (!titulo) {
      toast.error('El título no puede estar vacío');
      return;
    }
    const id = renameTarget.id;
    setRenaming(true);
    try {
      const { data } = await api.patch(`/insights/conversations/${id}`, { title: titulo });
      // Actualiza el item en el estado (title + recencia) sin recargar todo.
      setConversations((cs) =>
        cs.map((c) =>
          c.id === id ? { ...c, title: data?.title ?? titulo, updated_at: data?.updated_at ?? c.updated_at } : c,
        ),
      );
      toast.success('Conversación renombrada');
      setRenameTarget(null);
    } catch (e) {
      toast.error(apiError(e) || 'No se pudo renombrar la conversación');
    } finally {
      setRenaming(false);
    }
  };

  // Borrar un hilo: DELETE + quitar del estado; si era el activo, volver a nuevo.
  const borrarConversacion = async (id) => {
    try {
      await api.delete(`/insights/conversations/${id}`);
      setConversations((cs) => cs.filter((c) => c.id !== id));
      if (id === activeConversationId) {
        setMessages([]);
        setActiveConversationId(null);
      }
      toast.success('Conversación eliminada');
    } catch (e) {
      toast.error(apiError(e) || 'No se pudo borrar la conversación');
    }
  };

  const enviar = async (texto) => {
    const pregunta = (texto ?? input).trim();
    // Sin agente seleccionable (lista vacía) no se puede chatear (CA-6/CA-18).
    if (!pregunta || loading || !selectedAgent?.id) return;
    // El chat envía el UUID del agente; el backend lo traduce al slug del worker.
    const agentId = selectedAgent.id;
    const eraNuevo = activeConversationId == null;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: pregunta }]);
    setLoading(true);
    try {
      const { data } = await api.post('/insights/chat', {
        question: pregunta,
        agent: agentId,
        conversation_id: activeConversationId ?? undefined,
      });
      // Se guarda la pregunta original y el UUID del agente usado en el mensaje
      // del agente para que Regenerar y el feedback de pulgares dispongan de ellos.
      setMessages((m) => [...m, { role: 'agent', question: pregunta, agent: agentId, ...data }]);
      // Si el backend materializó un hilo nuevo, fijar el id y refrescar el
      // historial para que aparezca en el sidebar.
      if (data.conversation_id && eraNuevo) {
        setActiveConversationId(data.conversation_id);
        loadConversations();
      }
    } catch (e) {
      // El 422 del worker trae {status:"error", error:"…"}; el resto de 4xx/5xx
      // (chat y /conversations*) traen {detail:"…"} vía respondError.
      const detail = e?.response?.data?.error ?? apiError(e);
      setMessages((m) => [...m, { role: 'agent', error: detail }]);
      toast.error('No se pudo completar la consulta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      {/* Encabezado de pagina al estilo Toolkit: badge + titulo grande +
          descripcion muted. Da aire sobre el header global morado. */}
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-primary/10 p-2 text-primary">
          <HugeiconsIcon icon={AiInnovation01Icon} size={24} strokeWidth={1.5} aria-hidden={true} />
        </div>
        <h1 className="font-display text-3xl font-bold text-foreground">VIA Insights</h1>
      </div>
      <p className="mt-3 text-muted-foreground">
        Pregunta en lenguaje natural sobre los datos del negocio. Solo lectura.
      </p>

      {/* Card del chat (sidebar oscuro + area de conversacion). */}
      <div className="mt-6 flex h-[calc(100vh-15rem)] min-h-[32rem] overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm">
        {/* Sidebar desktop (fijo). Borde derecho para separar el panel del area
            de chat; sigue el tema de la app (claro/oscuro). */}
        <aside className="hidden w-72 shrink-0 border-r border-border md:block">
          <InsightsSidebar
            agents={agents}
            agentsState={agentsState}
            selectedAgent={selectedAgent}
            onSelectAgent={setSelectedAgent}
            conversations={conversations}
            convState={convState}
            activeConversationId={activeConversationId}
            onNueva={nuevaConversacion}
            onOpen={abrirConversacion}
            onRename={abrirRenombrar}
            onDelete={borrarConversacion}
          />
        </aside>

        {/* Sidebar móvil (overlay deslizante) */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-label="Menú de conversaciones">
            <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} aria-hidden={true} />
            <div id="insights-sidebar" className="absolute inset-y-0 left-0 w-72 max-w-[80%] shadow-xl">
              <InsightsSidebar
                agents={agents}
                agentsState={agentsState}
                selectedAgent={selectedAgent}
                onSelectAgent={setSelectedAgent}
                conversations={conversations}
                convState={convState}
                activeConversationId={activeConversationId}
                onNueva={nuevaConversacion}
                onOpen={abrirConversacion}
                onRename={abrirRenombrar}
                onDelete={borrarConversacion}
              />
            </div>
          </div>
        )}

        {/* Área de conversación (tema de la app) */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Barra superior ligera del card: en movil ofrece el boton hamburguesa
            que abre el overlay del sidebar; en desktop el sidebar es fijo y el
            titulo vive en el encabezado de pagina, por eso aqui no se repite. */}
          <header className="flex items-center gap-3 border-b border-border/60 px-4 py-3 md:hidden">
            <button
              type="button"
              onClick={() => setSidebarOpen((v) => !v)}
              aria-label="Abrir menú de conversaciones"
              aria-expanded={sidebarOpen}
              aria-controls="insights-sidebar"
              className="flex h-9 w-9 items-center justify-center rounded-md border border-border text-body transition hover:bg-muted hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1">
              <HugeiconsIcon icon={Menu01Icon} size={18} strokeWidth={1.5} aria-hidden={true} />
            </button>
            <span className="truncate text-sm font-semibold text-text">VIA Insights</span>
          </header>

          <div className="flex min-h-0 flex-1 flex-col gap-4 p-4 md:p-6">
            {/* Hilo de conversación */}
            <div ref={scrollRef} className="min-h-0 flex-1 space-y-6 overflow-y-auto pr-1">
              {messages.length === 0 && !loading && (
                <MotionSection className="flex h-full flex-col items-center justify-center text-center">
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <HugeiconsIcon icon={SparklesIcon} size={28} strokeWidth={1.5} aria-hidden={true} />
                  </div>
                  <h2 className="font-display text-2xl font-bold text-foreground">
                    Hola, {user?.name?.split(' ')[0] || 'bienvenido'}
                  </h2>
                  <p className="mt-2 max-w-md text-sm text-muted-foreground">
                    Soy el agente de datos comerciales de VIA. Pregúntame sobre ventas, canales o CRM y te respondo con
                    datos reales del Data Warehouse.
                  </p>
                  <div className="mt-6 flex flex-wrap justify-center gap-2">
                    {SUGERENCIAS.map((s) => (
                      <button
                        key={s}
                        onClick={() => enviar(s)}
                        className="rounded-full border border-border bg-card px-4 py-2 text-sm text-body transition hover:border-primary hover:bg-primary/5 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1">
                        {s}
                      </button>
                    ))}
                  </div>
                </MotionSection>
              )}

              {messages.map((msg, i) =>
                msg.role === 'user' ? (
                  <div key={i} className="flex justify-end">
                    <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-primary px-4 py-2.5 text-sm leading-relaxed text-white">
                      {msg.content}
                    </div>
                  </div>
                ) : (
                  <MensajeAgente key={i} msg={msg} onRegenerate={enviar} />
                ),
              )}

              {loading && (
                <div className="flex items-center gap-2 text-sm text-muted" role="status" aria-live="polite">
                  <HugeiconsIcon
                    icon={Loading02Icon}
                    size={18}
                    strokeWidth={1.5}
                    className="animate-spin"
                    aria-hidden={true}
                  />
                  Consultando los datos…
                </div>
              )}
            </div>

            {/* Caja de entrada */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                enviar();
              }}
              className="flex items-end gap-2 border-t border-border/60 pt-4">
              <label htmlFor="insights-pregunta" className="sr-only">
                Escribe tu pregunta sobre los datos
              </label>
              <textarea
                id="insights-pregunta"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    enviar();
                  }
                }}
                rows={1}
                placeholder="Escribe tu pregunta…"
                className="max-h-32 min-h-[44px] flex-1 resize-none rounded-xl border border-border bg-background px-4 py-3 text-sm text-text outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/30"
              />
              <Button
                type="submit"
                disabled={loading || !input.trim() || !selectedAgent?.id}
                className="h-11 px-4"
                aria-label="Enviar pregunta">
                <HugeiconsIcon icon={SentIcon} size={18} strokeWidth={1.5} aria-hidden={true} />
              </Button>
            </form>
            {/* Sin agentes accesibles no se puede chatear: aviso explícito (CA-6/CA-18). */}
            {!agentsState.loading && !selectedAgent?.id && (
              <p className="px-1 text-xs text-muted-foreground" role="status">
                No tienes acceso a ningún agente. Pide a un administrador que te conceda acceso para poder consultar.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Diálogo de renombrar conversación (design system). Se controla con
          renameTarget; al cerrar devuelve el foco al trigger automáticamente. */}
      <Dialog open={renameTarget != null} onOpenChange={(abierto) => !abierto && setRenameTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <form onSubmit={confirmarRenombrar}>
            <DialogHeader>
              <DialogTitle>Renombrar conversación</DialogTitle>
              <DialogDescription>Elige un nuevo nombre para esta conversación.</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="insights-renombrar-input" className="sr-only">
                Nuevo título
              </Label>
              <Input
                id="insights-renombrar-input"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                placeholder="Título de la conversación"
                autoFocus
                maxLength={200}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRenameTarget(null)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={renaming || !renameValue.trim()}>
                {renaming && (
                  <HugeiconsIcon
                    icon={Loading02Icon}
                    size={16}
                    strokeWidth={1.5}
                    className="mr-2 animate-spin"
                    aria-hidden={true}
                  />
                )}
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
